"use client";

import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { cn } from "@workspace/ui/lib/utils";

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      className
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

/**
 * Extract initials from username or email
 * - If username exists, split on spaces/dots and take first letter of each part
 * - Otherwise, split email on "." and take parts before "@"
 * - Returns 1-3 uppercase characters, limited by available parts
 */
function getInitials(
  username: string | undefined,
  email: string,
  maxChars: number = 2
): string {
  // If username exists, use it
  if (username && username.trim()) {
    const parts = username
      .trim()
      .split(/[\s.]+/)
      .filter(Boolean);
    const availableParts = parts.length;
    const charsToUse = Math.min(maxChars, availableParts, 3);

    return parts
      .slice(0, charsToUse)
      .map((p) => p![0]!.toUpperCase())
      .join("");
  }

  // Otherwise use email: split on ".", take before "@"
  const emailPrefix = email.split("@")[0]!;
  const emailParts = emailPrefix.split(".").filter(Boolean);
  const availableParts = emailParts.length;

  if (availableParts > 1) {
    // Multiple parts (e.g., "gaz.jessep@gmail.com" with maxChars=3 but only 2 parts -> "GJ")
    const charsToUse = Math.min(maxChars, availableParts, 3);
    return emailParts
      .slice(0, charsToUse)
      .map((p) => p![0]!.toUpperCase())
      .join("");
  } else {
    // Single part (e.g., "john@example.com" -> can only use 1 char "J")
    return emailPrefix[0]!.toUpperCase();
  }
}

export interface UserAvatarProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    "children"
  > {
  avatarUrl?: string | null;
  username?: string;
  email: string;
  /** Override auto-generated initials with user-chosen initials */
  initials?: string;
  maxInitials?: 1 | 2 | 3;
  backgroundColor?: string;
  textColor?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

const sizeClasses = {
  sm: "h-9 w-9 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
  xl: "h-16 w-16 text-lg",
};

/**
 * UserAvatar component with automatic fallback to text-based initials
 *
 * @example
 * <UserAvatar
 *   avatarUrl={user.avatarUrl}
 *   username={user.username}
 *   email={user.email}
 *   maxInitials={2}
 *   backgroundColor="#3b82f6"
 *   size="md"
 * />
 */
const UserAvatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  UserAvatarProps
>(
  (
    {
      avatarUrl,
      username,
      email,
      initials: initialsProp,
      maxInitials = 2,
      backgroundColor,
      textColor = "#ffffff",
      size = "md",
      className,
      ...props
    },
    ref
  ) => {
    const initials = initialsProp || getInitials(username, email, maxInitials);

    return (
      <Avatar ref={ref} className={cn(sizeClasses[size], className)} {...props}>
        {avatarUrl && <AvatarImage src={avatarUrl} alt={username || email} />}
        <AvatarFallback
          style={{
            backgroundColor: backgroundColor || undefined,
            color: textColor,
          }}
        >
          {initials}
        </AvatarFallback>
      </Avatar>
    );
  }
);
UserAvatar.displayName = "UserAvatar";

export { Avatar, AvatarImage, AvatarFallback, UserAvatar };
